import React, { useState, useEffect, useContext, Fragment } from 'react';
import { func, oneOf } from 'prop-types';
import classNames from 'classnames/bind';

import AutosuggestPart from 'components/AutosuggestPart';
import AutosaggestPopin from 'components/AutosaggestPopin';
import CarPlateSearch from 'components/CarPlateSearch';
import CarIncrementalSearch from 'components/CarIncrementalSearch';
import CarDetails from 'components/CarDetails';
import CountrySelector from 'components/CountrySelector';
import SwitcherVehicleType from 'components/SwitcherVehicleType';
import Hint from 'components/Hint';

import { withErrorBoundary } from 'containers/ErrorBoundary';

import { SearchContext } from 'context/SearchContext';
import { StaticTextContext } from 'context/StaticTextContext';

import {
  isGlobalSite,
  currentLanguage,
  currentCountry,
} from 'utils/currentLanguage';
import { gtmPushEvent } from 'utils/gtmPushEvent';
import { getBrowserHistory } from 'utils/historyPushSearch';
import { redirectToTechAssist } from 'utils/redirectToTechAssist';
import browserHistory, { isTechAssistPage } from 'utils/browserHistory';

import styles from './SearchByCar.module.scss';
import SearchIcon from 'media/icons/search.icon.svg';
import CheckIcon from 'media/icons/checkmark.icon.svg';

const cx = classNames.bind(styles);

const SearchByCar = ({ pageType, getArticlesIds, resetSearchResults }) => {
  const [partValue, setPartValue] = useState('');
  const [partData, setPartData] = useState([]);
  const {
    setIsErrorPart,
    setIsCarTooltip,
    setIsPartTooltip,
    setSearchCarQuery,
    isCarTooltip,
    isPartTooltip,
    isErrorPart,
    isSelectedCar,
    isSelectedCarPlate,
    partId,
    manuName,
    modelName,
    carId,
    carTypeName,
    partName,
    carName,
    carPlate,
    vehicleType,
    modelId,
    manuId,
  } = useContext(SearchContext);
  const contextSearch = useContext(SearchContext);
  const { labels } = useContext(StaticTextContext);

  const handleSearch = (event) => {
    event.preventDefault();
    const { search, hash } = browserHistory.location;
    const searchObj = {
      modelId,
      modelName,
      manuId,
      manuName,
      carId,
      carName,
      carTypeName,
      partName,
      partId,
    };

    // Send gtm search event
    const sharedOptions = {
      carBrandType: vehicleType === 'P' ? 'car' : 'truck',
      carBrandManufacturer: manuName,
      carBrandModel: modelName,
      carBrandVehicle: carTypeName,
      product: partName,
      country: currentCountry,
    };
    const pushCar = {
      ...sharedOptions,
      event: 'searchProductCarModel',
    };
    const pushCarPlate = {
      ...sharedOptions,
      event: 'searchProductCarPlateNumber',
    };
    const gtmData = isSelectedCarPlate ? pushCarPlate : pushCar;

    gtmPushEvent(gtmData);
    setIsCarTooltip(false);
    setIsPartTooltip(false);
    setSearchCarQuery(partName + ', ' + carName);

    if (pageType === 'home') {
      if (!isTechAssistPage) {
        redirectToTechAssist(getBrowserHistory(searchObj, search, hash));
        return null;
      }
      browserHistory.push({
        pathname: '/search',
        search: getBrowserHistory(searchObj, search, hash),
      });
    } else {
      getArticlesIds();
    }
  };

  useEffect(() => {
    if (carId && partId && pageType !== 'home') {
      getArticlesIds(contextSearch);
    }
  }, []);

  const isSelectedPart = partName && partName.length > 0;
  const isCarPlateSearch = currentLanguage === 'fr';
  const trimmedCarPlate = carPlate.replace(/-/g, '');

  return (
    <form
      onSubmit={handleSearch}
      className={cx('search-form', pageType, 'search-car', {
        'opened-tooltip': isPartTooltip || isCarTooltip,
        'car-plate-block': isCarPlateSearch
      })}
    >
      {isGlobalSite && (
        <div className={cx('form-item')}>
          <CountrySelector />
        </div>
      )}

      <SwitcherVehicleType resetSearchResults={resetSearchResults} />

      <div className={cx('control-wrap')}>
        <div
          className={cx('form-item', {
            'search-car-plate-wrap': isCarPlateSearch,
          })}
        >
          {isCarPlateSearch && (
            <Fragment>
              <CarPlateSearch
                isRequired={!carName && !manuName}
                pageType={pageType}
                setPartData={setPartData}
                setPartValue={setPartValue}
              />
              <div className={cx('separator')}>
                <div className={cx('separator-text')}>OU</div>
              </div>
            </Fragment>
          )}
          <CarIncrementalSearch
            pageType={pageType}
            isCarPlateSearch={isCarPlateSearch}
            updatePartData={setPartData}
            updatePartValue={setPartValue}
          />
        </div>
        <div className={cx('form-item', 'form-item-part')}>
          <div className={cx('item-part-wrap')}>
            <AutosaggestPopin
              tooltipStatus={isPartTooltip}
              toggleTooltip={setIsPartTooltip}
              inputLabel={labels.searchPartLabel}
            >
              <label className={cx('label')} htmlFor="search-part">
                {labels.searchPartLabel}
              </label>
              <div
                className={cx('form-control-wrap', {
                  'is-filled': isSelectedPart,
                })}
              >
                <AutosuggestPart
                  className={cx('form-control', {
                    'is-error': !isSelectedPart && isSelectedCar && isErrorPart,
                  })}
                  type="text"
                  id="search-part"
                  pageType={pageType}
                  disabled={!(isSelectedCar || trimmedCarPlate.length >= 7)}
                  setIsErrorPart={setIsErrorPart}
                  partValue={partValue}
                  partData={partData}
                  updatePartValue={setPartValue}
                  placeholder={labels.searchPartPlaceholder}
                  toggleTooltip={setIsPartTooltip}
                />
                <div className={cx('icon-check')}>
                  <CheckIcon />
                </div>
                <div
                  className={cx('error-message', {
                    'is-error': !isSelectedPart && isSelectedCar && isErrorPart,
                  })}
                >
                  {labels.errorPartField}
                </div>
                {pageType === 'home' && isSelectedCar && (
                  <Hint className={cx('hint', 'is-selected')}>
                    <span className={cx('hint-text')}>
                      {labels.tipPartInput}
                    </span>
                  </Hint>
                )}
                <button
                  className={cx('btn', 'ta-btn-primary', 'mobile-button')}
                  type="submit"
                  disabled={!isSelectedPart}
                >
                  <span className={cx('btn-icon')}>
                    <SearchIcon />
                  </span>
                </button>
              </div>
            </AutosaggestPopin>
          </div>

          <button
            type="submit"
            className={cx('btn', 'ta-btn-primary')}
            disabled={!isSelectedPart}
          >
            <span className={cx('btn-icon')}>
              <SearchIcon />
            </span>
            <span className={cx('btn-text')}>{labels.searchButtonText}</span>
          </button>
        </div>
      </div>
      {pageType === 'search' && <CarDetails />}
    </form>
  );
};

SearchByCar.propTypes = {
  pageType: oneOf(['home', 'search']),
  resetSearchResults: func.isRequired,
  getArticlesIds: func.isRequired,
};

SearchByCar.defaultProps = {
  pageType: 'home',
};

export default withErrorBoundary(SearchByCar);



// WEBPACK FOOTER //
// ./src/components/SearchByCar/SearchByCar.js